"use strict";
// Plain-language explanations of a plugin resolution, for owners (workbench
// Plugins section) and for pre-publish warnings. The registry reports
// structure (missing points, conflicts); this turns it into sentences that
// name plugins by title, never by point id.
Object.defineProperty(exports, "__esModule", { value: true });
exports.explainResolution = explainResolution;
exports.defaultOnAddons = defaultOnAddons;
function title(registry, id) {
    return registry.get(id)?.title ?? id;
}
function joinList(items, word) {
    if (items.length <= 1) {
        return items[0] ?? '';
    }
    return `${items.slice(0, -1).join(', ')} ${word} ${items[items.length - 1]}`;
}
/** "offSessionCharge" → "off session charge". */
function humanizeFeature(feature) {
    return feature.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
}
/** Titles of the plugins that could provide `point` (excluding `self`). */
function providerTitles(registry, point, self) {
    const ids = [...new Set(registry.providersOf(point).map((p) => p.plugin.id))].filter((id) => id !== self);
    return ids.map((id) => title(registry, id));
}
/** Plain-language problems for a resolution, most severe first. */
function explainResolution(resolution, registry) {
    const out = [];
    for (const m of resolution.missing) {
        const who = title(registry, m.plugin);
        const providers = providerTitles(registry, m.point, m.plugin);
        const what = registry.declaration(m.point)?.description;
        if (m.reason === 'features') {
            const features = joinList((m.requirement.features ?? []).map(humanizeFeature), 'and');
            out.push({
                level: 'error',
                plugin: m.plugin,
                message: `${who} needs ${features}, which none of the plugins turned on for it support.`,
            });
        }
        else if (providers.length === 1) {
            out.push({ level: 'error', plugin: m.plugin, message: `${who} needs ${providers[0]}, which isn't turned on.` });
        }
        else if (providers.length > 1) {
            out.push({
                level: 'error',
                plugin: m.plugin,
                message: `${who} needs one of ${joinList(providers, 'or')}, and none of them is turned on.`,
            });
        }
        else {
            out.push({
                level: 'error',
                plugin: m.plugin,
                message: `${who} needs something Dunea doesn't offer yet${what ? ` (${what.replace(/\.$/, '')})` : ''}.`,
            });
        }
    }
    for (const c of resolution.conflicts) {
        const what = registry.declaration(c.point)?.description;
        out.push({
            level: 'error',
            plugin: c.providers[0],
            message: `Only one of ${joinList(c.providers.map((id) => title(registry, id)), 'or')} can be on at a time${what ? ` (${what.replace(/\.$/, '').toLowerCase()})` : ''}. Turn one off.`,
        });
    }
    for (const cycle of resolution.cycles) {
        out.push({
            level: 'error',
            plugin: cycle[0],
            message: `${joinList(cycle.map((id) => title(registry, id)), 'and')} depend on each other, so they can't load. This is a Dunea bug; please report it.`,
        });
    }
    for (const id of resolution.unknown) {
        out.push({ level: 'warning', plugin: id, message: `"${id}" is not a plugin Dunea knows about; it is ignored.` });
    }
    for (const d of resolution.degraded) {
        const providers = providerTitles(registry, d.point, d.plugin);
        out.push({
            level: 'info',
            plugin: d.plugin,
            message: providers.length
                ? `${title(registry, d.plugin)} works without ${joinList(providers, 'or')}, with fewer features.`
                : `${title(registry, d.plugin)} works with fewer features.`,
        });
    }
    for (const i of resolution.inert) {
        const declarer = registry.declarerOf(i.point);
        out.push({
            level: 'info',
            plugin: i.plugin,
            message: `${title(registry, i.plugin)} does nothing until ${declarer ? title(registry, declarer.id) : 'its host plugin'} is turned on.`,
        });
    }
    return out;
}
/**
 * Host-only add-ons that are on by default for these enabled plugins: provider
 * plugins with no app code that react to events (e.g. Order emails for Shop).
 * They run unless the owner explicitly disables them, so a status view should
 * list them even without an app_plugins row.
 */
function defaultOnAddons(enabled, registry) {
    const on = new Set(enabled);
    return registry
        .all()
        .filter((m) => {
        if (m.kind !== 'provider' || !m.subscribes?.length || on.has(m.id)) {
            return false;
        }
        if (Object.values(m.targets ?? {}).some((t) => t.lockedPaths?.length)) {
            return false;
        }
        const points = Object.keys(m.provides ?? {});
        return points.length > 0 && points.some((p) => on.has(registry.declarerOf(p)?.id ?? ''));
    })
        .map((m) => m.id);
}
